/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useCallback, useEffect } from "react";
import type { FunctionComponent, JSX } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useNavigationService } from "../../hooks";
import {
  selectIsTvDevice,
  selectIsProfileSelectionRequired,
  useUnityDispatch,
  useUnitySelector
} from "../../../viewModel";
import {
  fetchQuickMenuShortcuts,
  selectIsShortcutsLoaded,
  UiDestinationTypeV4
} from "../../../viewModel/homescreen";
import { selectHasStartedUp } from "../../../viewModel/postSigninStartup";
import { MenuStrip, DESTINATION_ROUTE_MAP } from "../../HomeScreen/MenuStrip";
import { LocationUrls } from "../../../viewModel/navigation";
import "./NavLayout.scss";

/**
 * NavLayout — shared layout for all authenticated content screens.
 *
 * In browser mode: renders the MenuStrip persistently at the top so it
 * remains visible when navigating between screens (Guide, HomeScreen, etc.).
 * In SmartTV mode: renders the Outlet directly — each SmartTV screen owns
 * its menu rendering within the dimmer-based UI layer.
 *
 * Profile selection gate is handled by MainLayout parent before rendering.
 */
type TProfileSelectionNavigationState = {
  openedFromMenuStrip?: boolean;
};

const NavLayout: FunctionComponent = (): JSX.Element => {
  const { navigateTo } = useNavigationService();
  const location = useLocation();
  const dispatch = useUnityDispatch();
  const isTvDevice: boolean = useUnitySelector(selectIsTvDevice);
  const isShortcutsLoaded: boolean = useUnitySelector(selectIsShortcutsLoaded);
  const hasStartedUp: boolean = useUnitySelector(selectHasStartedUp);
  const isProfileSelectionRequired: boolean = useUnitySelector(
    selectIsProfileSelectionRequired
  );

  // Backstop for the quick-menu shortcuts the startup sequence fetches at its
  // Step 7, so the MenuStrip is populated on any screen NavLayout wraps
  // (HomeScreen, GuideScreen, etc.).
  //
  // Gated on `hasStartedUp` so this no longer races Step 7. It used to fetch
  // on mount whenever the shortcuts were absent, which they always are at that
  // point: NavLayout mounts as soon as the user is authenticated, while Step 7
  // waits behind device features. The result was two requests for the same
  // data on a healthy boot, and on a broken one a shortcuts request issued
  // before the `deviceFeatures` response carrying `msoServiceId` had arrived.
  //
  // Kept rather than deleted because Step 7 is fire-and-forget with no retry —
  // once the sequence has finished and the shortcuts are still missing, its
  // attempt failed and this is the only thing that will try again.
  //
  // This runs on both browser and SmartTV — hooks always execute before any
  // conditional return, so the isTvDevice guard below does not suppress this
  // effect. On SmartTV the shortcuts are consumed by HomeScreen's own MenuStrip.
  useEffect((): void => {
    if (hasStartedUp && !isShortcutsLoaded) {
      dispatch(fetchQuickMenuShortcuts());
    }
  }, [dispatch, hasStartedUp, isShortcutsLoaded]);

  const handleShortcutActivate = useCallback(
    (destination: string): void => {
      const route = DESTINATION_ROUTE_MAP[destination as UiDestinationTypeV4];
      if (route !== undefined) {
        navigateTo(route.url);
      }
    },
    [navigateTo]
  );

  if (isTvDevice) {
    return <Outlet />;
  }

  const isProfileSelectionScreen: boolean =
    location.pathname === LocationUrls.SELECT_PROFILE;
  const profileSelectionNavigationState =
    location.state as TProfileSelectionNavigationState | null;
  const isOpenedFromMenuStrip: boolean =
    profileSelectionNavigationState !== null &&
    profileSelectionNavigationState !== undefined &&
    profileSelectionNavigationState.openedFromMenuStrip === true;

  return (
    <div className="nav-layout">
      <div className="nav-layout__menu">
        <MenuStrip
          onNavigate={handleShortcutActivate}
          brandingOnly={
            isProfileSelectionScreen &&
            (!isOpenedFromMenuStrip || isProfileSelectionRequired)
          }
        />
      </div>
      <div className="nav-layout__content">
        <Outlet />
      </div>
    </div>
  );
};

export default NavLayout;
